import clsx from 'clsx';
import {
  Stack,
  Users,
  CreditCard,
  ChartBar,
  ClockCountdown,
  Desktop,
  DownloadSimple,
  ListChecks,
  Lightning,
  Plug,
  ShieldCheck,
  Briefcase,
  SquaresFour,
  Broadcast,
  Gift,
} from '@phosphor-icons/react';
import { useTranslation } from '@client-app/i18n';
import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';

import Icon from '@common/components/Icon';
import Tooltip from '@common/components/Tooltip';
import styles from './Sidebar.module.scss';
import WorkspaceSwitcher from './WorkspaceSwitcher';
import downloadPageImg from '@/assets/images/Download_page_img.png';
import referralStar from '@/assets/referral-star.png';
import ProductBrandLogo from '@/components/Brand/ProductBrandLogo';
import { useAdminContext } from '@/contexts/AdminContext';
import { Permission, useAuth } from '@/contexts/AuthContext';
import {
  buildBrandDetailPath,
  buildBrandWorkspacePath,
} from '@/components/BrandWorkspace/brandWorkspaceRoute';
import {
  formatPlanDate,
  formatTrialExpirationText,
} from '@/contexts/AuthContext/planTextUtils';
import { resolveWorkspaceSubscriptionDisplayState } from '@/utils/workspaceSubscriptionDisplayState';
import { getAdminProductFeatures } from '@/utils/adminProductFeatures';
import { isPluginSetupSidebarHintVisible } from '@/components/PluginSetup/pluginSetupSidebarHint';
import { isMarketIntelligenceAdminAvailable } from '@/utils/marketIntelligenceAvailability';

export interface SidebarMenuItem {
  name: string;
  path?: string;
  onClick?: () => void;
  icon: React.ReactNode;
  end?: boolean;
}

interface SidebarProps {
  menuItems?: SidebarMenuItem[];
  bottomItems?: SidebarMenuItem[];
  forceCollapsed?: boolean;
  collapseOnNarrowViewport?: boolean;
}

type SidebarHoverCard = 'trial' | 'download';

const DOWNLOAD_HOVER_CARD_WIDTH = 300;
const DOWNLOAD_HOVER_CARD_HEIGHT = 342;
const PLUGIN_SETUP_SIDEBAR_HINT_DURATION_MS = 5000;
const NARROW_VIEWPORT_QUERY = '(max-width: 768px)';

const getHoverCardDimensions = (card: SidebarHoverCard) => {
  if (card === 'download') {
    return {
      width: DOWNLOAD_HOVER_CARD_WIDTH,
      height: DOWNLOAD_HOVER_CARD_HEIGHT,
    };
  }

  return { width: 240, height: 116 };
};

const UtilityButton = ({
  icon,
  label,
  collapsed,
}: {
  icon: React.ReactNode;
  label: string;
  collapsed: boolean;
}) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    className={clsx(
      'flex w-full items-center rounded-lg border border-[#1A162F]/10 bg-[#1A162F]/5 text-[#1A162F] transition-colors hover:bg-[#1A162F]/10',
      collapsed ? 'justify-center p-2' : 'gap-2 px-3 py-2',
    )}
  >
    <span className="shrink-0">{icon}</span>
    {!collapsed ? (
      <span className="truncate text-xs font-medium">{label}</span>
    ) : null}
  </button>
);

const SidebarItem = ({
  icon: IconNode,
  label,
  active,
  onClick,
  onMouseEnter,
  onFocus,
  collapsed,
  rightSlot,
  hierarchy,
  interactive = true,
}: any) => {
  const itemContent = (
    <div
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onFocus={onFocus}
      aria-label={label}
      className={clsx(
        styles.sidebarItem,
        collapsed && styles.collapsed,
        active && styles.active,
        hierarchy === 'root' && styles.hierarchyRoot,
        hierarchy === 'child' && styles.hierarchyChild,
        !interactive && styles.nonInteractive,
        'group',
      )}
    >
      <span className={styles.sidebarItemIcon} aria-hidden="true">
        {typeof IconNode === 'string' ? (
          <img src={IconNode} alt="" />
        ) : (
          IconNode
        )}
      </span>
      {!collapsed && <span className={styles.sidebarItemLabel}>{label}</span>}
      {!collapsed && rightSlot}
    </div>
  );

  if (!collapsed) return itemContent;

  return (
    <Tooltip
      content={label}
      side="right"
      sideOffset={10}
      align="center"
      showArrow={false}
    >
      {itemContent}
    </Tooltip>
  );
};

import UserMenu from '../UserMenu/UserMenu';

const Sidebar: React.FC<SidebarProps> = ({
  collapseOnNarrowViewport = false,
  forceCollapsed,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, locale } = useTranslation();
  const { currentWorkspace, permissions, userBilling, user } = useAuth();
  const { modal } = useAdminContext();
  const adminProductFeatures = getAdminProductFeatures();
  const showMarketIntelligence = isMarketIntelligenceAdminAvailable() && Boolean(user);
  const showDesktopDownload = adminProductFeatures.desktopDownload;
  const showDownloadEntry = showDesktopDownload;
  const downloadEntryLabel = t('website.settingsModal.downloads');
  const showPluginInstallEntry = adminProductFeatures.pluginInstall;
  const pluginInstallLabel = t('website.pluginInstall.label');
  const pluginInstallHint = t('website.pluginInstall.sidebarHint');
  const isPluginSetupSidebarHintOpen = isPluginSetupSidebarHintVisible(
    modal.pluginSetupSidebarHintState,
  );

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => Boolean(
    forceCollapsed
      || (collapseOnNarrowViewport
        && typeof window !== 'undefined'
        && window.matchMedia?.(NARROW_VIEWPORT_QUERY).matches),
  ));

  const [activeHoverCard, setActiveHoverCard] = useState<SidebarHoverCard | null>(null);
  const [hoverCardPosition, setHoverCardPosition] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    width: number;
  }>({ left: 0, width: 0 });
  const sidebarRef = useRef<HTMLDivElement>(null);
  const trialItemRef = useRef<HTMLDivElement>(null);
  const downloadItemRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (
      modal.pluginSetupSidebarHintState !== 'requested' ||
      !showPluginInstallEntry
    ) {
      return;
    }

    modal.confirmPluginSetupSidebarHintVisible();
  }, [
    modal.confirmPluginSetupSidebarHintVisible,
    modal.pluginSetupSidebarHintState,
    showPluginInstallEntry,
  ]);

  useEffect(() => {
    if (!isPluginSetupSidebarHintOpen) return undefined;

    const dismissTimer = window.setTimeout(
      modal.dismissPluginSetupSidebarHint,
      PLUGIN_SETUP_SIDEBAR_HINT_DURATION_MS,
    );
    return () => window.clearTimeout(dismissTimer);
  }, [isPluginSetupSidebarHintOpen, modal.dismissPluginSetupSidebarHint]);

  const openPluginSetup = () => {
    modal.dismissPluginSetupSidebarHint();
    modal.openModal('pluginSetup');
  };

  const pluginSetupTooltipProps = {
    content: isPluginSetupSidebarHintOpen ? pluginInstallHint : pluginInstallLabel,
    side: 'right' as const,
    sideOffset: 12,
    contentClassName: isPluginSetupSidebarHintOpen ? styles.pluginSetupHint : undefined,
    open: isPluginSetupSidebarHintOpen ? true : undefined,
    onOpenChange: (open: boolean) => {
      if (!open) modal.dismissPluginSetupSidebarHint();
    },
  };

  const handleHoverCardEnter = (
    card: SidebarHoverCard,
    anchorRef: React.RefObject<HTMLDivElement>,
  ) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      const { width: cardWidth, height: cardHeight } = getHoverCardDimensions(card);
      if (isSidebarCollapsed) {
        let top = rect.top;
        const padding = 16;
        if (top + cardHeight > window.innerHeight - padding) {
          top = Math.max(padding, window.innerHeight - cardHeight - padding);
        }
        const left = rect.right + 8;
        setHoverCardPosition({
          top: top,
          left,
          width:
            card === 'trial'
              ? cardWidth
              : Math.min(cardWidth, window.innerWidth - left - padding),
        });
      } else {
        const sidebarRect = sidebarRef.current?.getBoundingClientRect();
        const horizontalInset = 12;
        const left = sidebarRect
          ? sidebarRect.left + horizontalInset
          : Math.max(12, rect.right - 240);
        const defaultWidth = sidebarRect
          ? Math.max(220, sidebarRect.width - horizontalInset * 2)
          : 240;
        setHoverCardPosition({
          bottom: window.innerHeight - rect.top + 8,
          left,
          width:
            card === 'trial'
              ? defaultWidth
              : Math.min(cardWidth, window.innerWidth - left - horizontalInset),
        });
      }
    }
    setActiveHoverCard(card);
  };

  const handleHoverCardLeave = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setActiveHoverCard(null);
    }, 150);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (forceCollapsed) setIsSidebarCollapsed(true);
  }, [forceCollapsed]);

  useEffect(() => {
    if (!collapseOnNarrowViewport || typeof window === 'undefined' || !window.matchMedia) {
      return undefined;
    }

    const viewportQuery = window.matchMedia(NARROW_VIEWPORT_QUERY);
    const collapseForNarrowViewport = (event: MediaQueryListEvent | MediaQueryList) => {
      if (event.matches) setIsSidebarCollapsed(true);
    };

    collapseForNarrowViewport(viewportQuery);
    viewportQuery.addEventListener('change', collapseForNarrowViewport);
    return () => viewportQuery.removeEventListener('change', collapseForNarrowViewport);
  }, [collapseOnNarrowViewport]);

  const isChinese = locale === 'zh-CN';
  const brandNavigationCopy = isChinese
    ? {
        brand: '业务管理',
        workspace: '工作区',
        allBrands: '业务组',
        myBrands: '我的业务组',
      }
    : {
        brand: 'Business management',
        workspace: 'Workspace',
        allBrands: 'Business groups',
        myBrands: 'My Business groups',
      };
  const canManageSubscription = !permissions.isLoading
    && (permissions.isOwner || permissions.checkPermissions([Permission.SUBSCRIPTION_MANAGE]));
  const canOpenPricing = adminProductFeatures.pricingModal;
  const trialEndsAt =
    currentWorkspace?.subscription?.trial_end_at ||
    userBilling.userPlan?.trialEndAt ||
    null;
  const trialState = resolveWorkspaceSubscriptionDisplayState(currentWorkspace?.subscription, {
    isTrial: userBilling.isTrial,
    status: userBilling.planText.status,
  });
  const needsSubscription = trialState !== 'not-trial';
  const subscriptionActionLabel = trialState === 'expired-paid'
    ? t('website.planText.renewSubscription')
    : t('website.planText.startSubscription');
  const shouldUseBillingPlanText =
    userBilling.planText.status === 'trial' && Boolean(userBilling.planText.expirationText);
  const freeTrialMessage = trialState === 'expired-paid'
    ? t('website.subscriptionExpired.expiredDialogTitle')
    : trialState === 'expired-trial' && trialEndsAt
      ? t('website.planText.freeTrialExpiredOn', { date: formatPlanDate(trialEndsAt, locale) })
      : shouldUseBillingPlanText
        ? userBilling.planText.expirationText
        : formatTrialExpirationText(trialEndsAt, new Date(), locale, t);
  const showFreeTrialCard = Boolean(
    !isSidebarCollapsed &&
    canManageSubscription &&
    canOpenPricing &&
    needsSubscription &&
    freeTrialMessage,
  );
  const showTrialEntry = Boolean(
    canManageSubscription &&
    canOpenPricing &&
    needsSubscription &&
    freeTrialMessage,
  );

  const openPricing = () => {
    if (!canOpenPricing || !canManageSubscription) return;
    setActiveHoverCard(null);
    modal.openModal('pricing');
  };

  const openReferral = () => {
    if (needsSubscription || permissions.isLoading) return;
    setActiveHoverCard(null);
    modal.openModal('referral');
  };

  const showReferralEntry = Boolean(
    !adminProductFeatures.pluginInstall && currentWorkspace?.id && !needsSubscription && !permissions.isLoading,
  );
  const showReferralCard = Boolean(
    !isSidebarCollapsed &&
    showReferralEntry &&
    !showFreeTrialCard,
  );
  const useCompactEnglishReferralCard = locale === 'en';
  const collapsedReferralTrigger = showReferralEntry ? (
    <Tooltip content={t('website.referralCard.collapsedTooltip')} side="right">
      <button
        type="button"
        aria-label={t('website.referralCard.collapsedTooltip')}
        title={t('website.referralCard.collapsedTooltip')}
        onClick={openReferral}
        className="flex h-10 w-10 items-center justify-center rounded-xl text-[#4D4865] transition-colors hover:bg-white hover:text-[#1A162F]"
        data-testid="sidebar-collapsed-referral-trigger"
      >
        <Gift size={19} weight="regular" />
      </button>
    </Tooltip>
  ) : null;

  const downloadsTrigger = showDownloadEntry ? (
    <div
      ref={downloadItemRef}
      onMouseEnter={() => handleHoverCardEnter('download', downloadItemRef)}
      onMouseLeave={handleHoverCardLeave}
      className="relative"
    >
      <button
        type="button"
        aria-label={downloadEntryLabel}
        title={downloadEntryLabel}
        className="flex h-9 w-9 items-center justify-center rounded-[10px] text-[#4D4865] transition-colors hover:bg-black/[0.045] hover:text-[#1A162F]"
      >
        <Desktop size={18} weight="regular" />
      </button>
    </div>
  ) : null;

  const pluginInstallTrigger = showPluginInstallEntry ? (
    <Tooltip {...pluginSetupTooltipProps}>
      <button
        type="button"
        aria-label={pluginInstallLabel}
        title={pluginInstallLabel}
        onClick={openPluginSetup}
        className="flex h-9 w-9 items-center justify-center rounded-[10px] text-[#4D4865] transition-colors hover:bg-black/[0.045] hover:text-[#1A162F]"
      >
        <Plug size={18} weight="regular" />
      </button>
    </Tooltip>
  ) : null;

  const collapsedTrialTrigger = showTrialEntry ? (
    <div
      ref={trialItemRef}
      onMouseEnter={() => handleHoverCardEnter('trial', trialItemRef)}
      onMouseLeave={handleHoverCardLeave}
      className="relative"
    >
      <button
        type="button"
        aria-label={freeTrialMessage}
        title={freeTrialMessage}
        className="flex h-10 w-10 items-center justify-center rounded-xl text-[#4D4865] transition-colors hover:bg-white hover:text-[#1A162F]"
        onClick={openPricing}
      >
        <ClockCountdown size={19} weight="regular" />
      </button>
    </div>
  ) : null;

  const collapsedDownloadsTrigger = showDownloadEntry ? (
    <div
      ref={downloadItemRef}
      onMouseEnter={() => handleHoverCardEnter('download', downloadItemRef)}
      onMouseLeave={handleHoverCardLeave}
      className="relative"
    >
      <button
        type="button"
        aria-label={downloadEntryLabel}
        title={downloadEntryLabel}
        className="flex h-10 w-10 items-center justify-center rounded-xl text-[#4D4865] transition-colors hover:bg-white hover:text-[#1A162F]"
      >
        <Desktop size={19} weight="regular" />
      </button>
    </div>
  ) : null;

  const collapsedPluginInstallTrigger = showPluginInstallEntry ? (
    <Tooltip {...pluginSetupTooltipProps}>
      <button
        type="button"
        aria-label={pluginInstallLabel}
        title={pluginInstallLabel}
        onClick={openPluginSetup}
        className="flex h-10 w-10 items-center justify-center rounded-xl text-[#4D4865] transition-colors hover:bg-white hover:text-[#1A162F]"
      >
        <Plug size={19} weight="regular" />
      </button>
    </Tooltip>
  ) : null;
  const userMenuUtilityTrigger = showPluginInstallEntry
    ? pluginInstallTrigger
    : downloadsTrigger;

  return (
    <div
      ref={sidebarRef}
      className={clsx(styles.sidebar, isSidebarCollapsed && styles.collapsed)}
    >
      <div className={styles.logoSection}>
        {!isSidebarCollapsed && (
          <div
            style={{
              display: 'flex',
              height: 40,
              paddingLeft: 16,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ProductBrandLogo size="24px" />
          </div>
        )}
        <button
          className={styles.toggleButton}
          onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        >
          <Icon
            tips={
              !isSidebarCollapsed ? t('sidebar.collapse') : t('sidebar.expand')
            }
            side="right"
            name={'collapse'}
          />
        </button>
      </div>

      <div className="mb-1.5 shrink-0 px-2.5 py-1.5">
        <WorkspaceSwitcher
          collapsed={isSidebarCollapsed}
          onOpenSettingsTab={(tab) => {
            navigate(`/${tab}`);
          }}
        />
      </div>

      <div className={styles.scrollArea}>
        {adminProductFeatures.brandManagement ? (
          <>
            <div className={styles.categoryHeader}>
              {brandNavigationCopy.brand}
            </div>
            <SidebarItem
              icon={<Briefcase size={20} weight="regular" />}
              label={brandNavigationCopy.allBrands}
              active={location.pathname.startsWith('/projects')}
              onClick={() => navigate(buildBrandWorkspacePath())}
              collapsed={isSidebarCollapsed}
              hierarchy="root"
            />
            {adminProductFeatures.workspaceRecord ? (
              <SidebarItem
                icon={<ListChecks size={20} weight="regular" />}
                label={t('website.settingsModal.valueLedger')}
                active={location.pathname === '/value-ledger'}
                onClick={() => navigate('/value-ledger')}
                collapsed={isSidebarCollapsed}
              />
            ) : null}
            {showMarketIntelligence ? (
              <SidebarItem
                icon={<Broadcast size={20} weight="regular" />}
                label={t('website.marketIntelligence.title')}
                active={location.pathname === '/market-intelligence'}
                onClick={() => navigate('/market-intelligence')}
                collapsed={isSidebarCollapsed}
              />
            ) : null}
            {adminProductFeatures.workspaceSkills ? (
              <SidebarItem
                icon={<Lightning size={20} weight="regular" />}
                label={t('website.settingsModal.workspaceSkills')}
                active={location.pathname === '/workspace-skills'}
                onClick={() => navigate('/workspace-skills')}
                collapsed={isSidebarCollapsed}
              />
            ) : null}
            {adminProductFeatures.auditLog ? (
              <SidebarItem
                icon={<ShieldCheck size={20} weight="regular" />}
                label={t('website.settingsModal.audit')}
                active={location.pathname === '/audit-log'}
                onClick={() => navigate('/audit-log')}
                collapsed={isSidebarCollapsed}
              />
            ) : null}
          </>
        ) : null}

        <div className={styles.categoryHeader}>
          {brandNavigationCopy.workspace}
        </div>
        <div className={styles.workspaceNavigationGroup}>
          <SidebarItem
            icon={<Users size={20} weight="regular" />}
            label={t('website.settingsModal.adminUsers')}
            active={location.pathname === '/members'}
            onClick={() => navigate('/members')}
            collapsed={isSidebarCollapsed}
          />
          <SidebarItem
            icon={<Stack size={20} weight="regular" />}
            label={t('website.settingsModal.workspaceSettings')}
            active={location.pathname === '/workspace-settings'}
            onClick={() => navigate('/workspace-settings')}
            collapsed={isSidebarCollapsed}
          />
          <SidebarItem
            icon={<SquaresFour size={20} weight="regular" />}
            label={t('website.settingsModal.adminIntegrations')}
            active={location.pathname === '/integrations'}
            onClick={() => navigate('/integrations')}
            collapsed={isSidebarCollapsed}
          />
          {adminProductFeatures.billingAndPlans ? (
            <SidebarItem
              icon={<CreditCard size={20} weight="regular" />}
              label={t('website.settingsModal.billingAndPlans')}
              active={location.pathname === '/billing'}
              onClick={() => navigate('/billing')}
              collapsed={isSidebarCollapsed}
            />
          ) : null}
          {adminProductFeatures.usage ? (
            <SidebarItem
              icon={<ChartBar size={20} weight="regular" />}
              label={t('website.settingsModal.usage')}
              active={location.pathname === '/usage'}
              onClick={() => navigate('/usage')}
              collapsed={isSidebarCollapsed}
            />
          ) : null}
        </div>
      </div>

      {showFreeTrialCard ? (
        <div className="shrink-0 px-3 pb-3">
          <div className="rounded-[16px] bg-[#071F22] p-3 shadow-[0_10px_28px_rgba(7,31,34,0.18)]">
            <p className="text-[13px] font-medium leading-5 text-white/80">
              {freeTrialMessage}
            </p>
            <button
              type="button"
              onClick={openPricing}
              className="mt-3 flex h-9 w-full items-center justify-center rounded-[10px] bg-white px-3 text-[13px] font-semibold text-[#1A162F] shadow-sm transition-colors hover:bg-[#F4F5F6]"
            >
              {subscriptionActionLabel}
            </button>
          </div>
        </div>
      ) : null}

      {showReferralCard ? (
        <div
          className={clsx('shrink-0 pb-2', useCompactEnglishReferralCard ? 'px-2' : 'px-3')}
          data-testid="sidebar-referral-card"
        >
          <button
            type="button"
            onClick={openReferral}
            className={clsx(
              styles.referralCard,
              'group flex w-full items-center justify-between py-2.5 text-left',
              useCompactEnglishReferralCard ? 'gap-1.5 px-2.5' : 'gap-2.5 px-3',
            )}
            data-testid="sidebar-referral-btn"
          >
            <div className={clsx(styles.referralCardContent, 'min-w-0 flex-1')}>
              <div className={clsx(
                styles.referralCardTitle,
                'font-semibold tracking-tight',
                useCompactEnglishReferralCard ? 'text-[12px]' : 'text-[13px]',
              )}>
                {t('website.referralCard.title')}
              </div>
              <div className={clsx(
                styles.referralCardDesc,
                'mt-0.5 text-[11px] leading-4',
                useCompactEnglishReferralCard && 'tracking-tight',
              )}>
                {t('website.referralCard.description')}
              </div>
            </div>
            <div className={styles.referralStarWrap}>
              <img
                src={referralStar}
                alt=""
                className={clsx(
                  styles.referralStarIcon,
                  useCompactEnglishReferralCard ? 'h-8 w-8' : 'h-9 w-9',
                )}
                draggable={false}
              />
            </div>
          </button>
        </div>
      ) : null}

      {isSidebarCollapsed ? (
        <div className="shrink-0 px-2 pb-2">
          <div className="flex flex-col items-center gap-1.5">
            {collapsedTrialTrigger}
            {collapsedReferralTrigger}
            {collapsedDownloadsTrigger}
            {collapsedPluginInstallTrigger}
          </div>
        </div>
      ) : null}

      <UserMenu
        collapsed={isSidebarCollapsed}
        showSettings
        hideEmail
        rightSlot={isSidebarCollapsed ? undefined : userMenuUtilityTrigger}
        onOpenSettingsTab={(tab) => navigate(`/${tab}`)}
      />

      {activeHoverCard &&
        createPortal(
          <div
            onMouseEnter={() => {
              if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
              }
            }}
            onMouseLeave={handleHoverCardLeave}
            className={clsx(
              'fixed overflow-hidden z-[99999] animate-in fade-in duration-200',
              activeHoverCard === 'trial'
                ? 'rounded-[16px] bg-[#071F22] p-3 shadow-[0_10px_28px_rgba(7,31,34,0.18)]'
                : 'rounded-[18px] border border-[#E8EBF3] bg-white p-4 shadow-[0_12px_28px_rgba(26,22,47,0.08)]',
            )}
            style={{
              top:
                hoverCardPosition.top !== undefined
                  ? `${hoverCardPosition.top}px`
                  : undefined,
              bottom:
                hoverCardPosition.bottom !== undefined
                  ? `${hoverCardPosition.bottom}px`
                  : undefined,
              left: `${hoverCardPosition.left}px`,
              width: `${hoverCardPosition.width}px`,
            }}
          >
            {activeHoverCard === 'trial' ? (
              <div className="space-y-3">
                <p className="text-left text-[13px] font-medium leading-5 text-white/80">
                  {freeTrialMessage}
                </p>
                <button
                  type="button"
                  onClick={openPricing}
                  className="inline-flex h-9 w-full items-center justify-center rounded-[10px] bg-white px-3 text-[13px] font-semibold text-[#1A162F] shadow-sm transition-colors hover:bg-[#F4F5F6]"
                >
                  {subscriptionActionLabel}
                </button>
              </div>
            ) : (
              <>
                <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-[#F8F9FC] border border-[#ECEFF5]">
                  <img
                    src={downloadPageImg}
                    alt="Bustly Desktop Preview"
                    className="h-full w-full object-contain object-top"
                  />
                </div>
                <div className="mt-3 space-y-3">
                  <p className="px-1 text-left text-[12px] leading-relaxed text-[#666F8D]">
                    {t('website.downloads.downloadDescriptionShort')}
                  </p>
                  <button
                    type="button"
                    onClick={() => window.open('https://www.bustly.ai/download', '_blank')}
                    className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-[#1A162F] px-4 text-[13px] font-medium text-white transition-colors hover:bg-[#231D3E]"
                  >
                    <DownloadSimple size={16} weight="bold" />
                    <span>{t('website.downloads.downloadClient')}</span>
                  </button>
                </div>
              </>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
};

export default Sidebar;
